import { useCallback, useEffect, useState } from 'react';
import { Button, Empty, Modal, Skeleton, Spin, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';

import {
  generatePartnerShopPlannerContent,
  getPartnerShopPlannerContents,
  selectPartnerShopPlannerContent,
  type PartnerShopListItem,
  type PartnerShopPlannerContentListItem,
} from '@/api/partnerShops';
import PlannerContentSection from './PlannerContentSection';

const { Text } = Typography;

interface PlannerContentModalProps {
  partnerShop: PartnerShopListItem | null;
  onClose: () => void;
}

export default function PlannerContentModal({
  partnerShop,
  onClose,
}: PlannerContentModalProps) {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const [items, setItems] = useState<PartnerShopPlannerContentListItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const loadPlannerContents = useCallback(async (
    nextSelectedId?: number
  ) => {
    if (!partnerShop) return;

    try {
      setLoading(true);
      const result = await getPartnerShopPlannerContents(partnerShop.id, {
        page: 1,
        rows: 100,
      });
      const nextItems = result.list || [];

      setItems(nextItems);
      setSelectedId((currentSelectedId) => {
        if (
          nextSelectedId &&
          nextItems.some((item) => item.id === nextSelectedId)
        ) {
          return nextSelectedId;
        }

        if (
          currentSelectedId &&
          nextItems.some((item) => item.id === currentSelectedId)
        ) {
          return currentSelectedId;
        }

        return nextItems[0]?.id || null;
      });
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : '플래너 콘텐츠 목록을 불러오지 못했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, [partnerShop]);

  useEffect(() => {
    if (!partnerShop) {
      setItems([]);
      setSelectedId(null);
      return;
    }

    loadPlannerContents();
  }, [partnerShop, loadPlannerContents]);

  const handleGenerate = async () => {
    if (!partnerShop) return;

    try {
      setGenerating(true);
      const generatedContent = await generatePartnerShopPlannerContent(
        partnerShop.id
      );

      message.success('새 플래너 추천 콘텐츠를 생성했습니다.');
      await loadPlannerContents(generatedContent.id);
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : '플래너 콘텐츠 생성 중 오류가 발생했습니다.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const selectedItem = items.find((item) => item.id === selectedId) || null;

  const handleSelectPlannerContent = async () => {
    if (!partnerShop || !selectedItem) return;

    try {
      setSelecting(true);
      await selectPartnerShopPlannerContent(partnerShop.id, selectedItem.id);

      message.success('선택한 콘텐츠를 플래너 추천 콘텐츠로 반영했습니다.');
      await loadPlannerContents(selectedItem.id);
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : '플래너 추천 콘텐츠 반영 중 오류가 발생했습니다.'
      );
    } finally {
      setSelecting(false);
    }
  };

  return (
    <Modal
      title={
        partnerShop
          ? `${partnerShop.shopName} 플래너 추천 컨텐츠 생성`
          : '플래너 추천 컨텐츠 생성'
      }
      open={Boolean(partnerShop)}
      width="min(1480px, calc(100vw - 48px))"
      footer={null}
      onCancel={onClose}
      destroyOnHidden
      styles={{ body: { paddingTop: 12 } }}
    >
      <ModalToolbar>
        <Text type="secondary">
          생성 이력을 선택하면 우측에서 실제 노출 형태를 확인할 수 있습니다.
        </Text>
        <Button type="primary" loading={generating} onClick={handleGenerate}>
          새로 생성
        </Button>
      </ModalToolbar>

      <ModalBody>
        <HistoryPanel>
          <HistoryTitle>
            <Text strong>생성 이력</Text>
            <Tag>{items.length.toLocaleString()}개</Tag>
          </HistoryTitle>

          <HistoryScroll>
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : items.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="생성된 콘텐츠가 없습니다." />
            ) : (
              items.map((item, index) => (
                <HistoryItem
                  key={item.id}
                  type="button"
                  $selected={item.id === selectedId}
                  onClick={() => setSelectedId(item.id)}
                >
                  <HistoryItemTop>
                    <strong>콘텐츠 #{item.id}</strong>
                    <HistoryTags>
                      {Number(item.currentPlannerContentYn) === 1 && (
                        <Tag color="green">반영 중</Tag>
                      )}
                      {index === 0 && <Tag color="blue">최신</Tag>}
                    </HistoryTags>
                  </HistoryItemTop>
                  <span>
                    {dayjs(item.creationTime).isValid()
                      ? dayjs(item.creationTime).format('YYYY.MM.DD HH:mm')
                      : item.creationTime}
                  </span>
                  <small>
                    섹션 {(item.contentJson.sections || []).length}개
                  </small>
                </HistoryItem>
              ))
            )}
          </HistoryScroll>
        </HistoryPanel>

        <PreviewPanel>
          <PreviewToolbar>
            <div>
              <Text strong>콘텐츠 미리보기</Text>
              {selectedItem && (
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  #{selectedItem.id}
                </Text>
              )}
            </div>
            <Button
              type="primary"
              loading={selecting}
              disabled={
                !selectedItem ||
                Number(selectedItem.currentPlannerContentYn) === 1
              }
              onClick={handleSelectPlannerContent}
            >
              {Number(selectedItem?.currentPlannerContentYn) === 1
                ? '현재 반영 중'
                : '플래너 추천 컨텐츠로 반영'}
            </Button>
          </PreviewToolbar>

          <PreviewScroll>
          {loading && items.length === 0 ? (
            <CenterContent><Spin /></CenterContent>
          ) : selectedItem ? (
            <PlannerContentSection content={selectedItem.contentJson} />
          ) : (
            <CenterContent>
              <Empty description="좌측에서 콘텐츠를 선택하거나 새로 생성해 주세요." />
            </CenterContent>
          )}
          </PreviewScroll>
        </PreviewPanel>
      </ModalBody>
    </Modal>
  );
}

const ModalToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 290px minmax(0, 1fr);
  height: min(74vh, 860px);
  min-height: 560px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  background: #f5f5f6;
`;

const HistoryPanel = styled.aside`
  display: flex;
  min-height: 0;
  flex-direction: column;
  border-right: 1px solid #e8e8e8;
  background: #fff;
`;

const HistoryTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const HistoryScroll = styled.div`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding: 12px;
`;

const HistoryItem = styled.button<{ $selected: boolean }>`
  width: 100%;
  padding: 13px 14px;
  border: 1px solid ${({ $selected }) => ($selected ? '#1677ff' : '#e8e8e8')};
  border-radius: 10px;
  background: ${({ $selected }) => ($selected ? '#e6f4ff' : '#fff')};
  color: #252525;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: #4096ff;
  }

  > span,
  > small {
    display: block;
    margin-top: 5px;
    color: #777;
    font-size: 12px;
  }
`;

const HistoryItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

`;

const HistoryTags = styled.div`
  display: flex;
  gap: 4px;

  .ant-tag {
    margin-inline-end: 0;
  }
`;

const PreviewPanel = styled.main`
  display: flex;
  min-height: 0;
  min-width: 0;
  flex-direction: column;
`;

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  border-bottom: 1px solid #e8e8e8;
  background: #fff;
`;

const PreviewScroll = styled.div`
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 28px;
`;

const CenterContent = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
